import { call, put, select, takeLatest } from 'redux-saga/effects';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  getCartDetailsFromCache,
  getCartInventoryData,
  getInventoryFromCache,
} from '@tcp/core/src/services/abstractors/SFCC/InYourBag';
import { getLocalStorage } from '@tcp/core/src/utils/utils.web';
import { prepareInventoryCartData, prepareOrderItems } from '../utils';
import { setInYourBagItems } from './InYourBag.actions';
import IN_YOUR_BAG, {
  cartInventoryTimestampKey,
  cartTimestampKey,
  graphQLCacheKey,
  inventoryTimestampKey,
} from './InYourBag.constants';
import { isPlccUser } from '../../../account/User/container/User.selectors';
import {
  getIsPersistPointsApiEnabled,
  getLoyaltyPointsMultiplier,
} from '../../../../../reduxStore/selectors/session.selectors';

/**
 * @param {array} productItems: cart items to prepare.
 * @param {array} inventory: check the inventory against the cart items.
 * @returns: consolidated object which contains total Items in cart, InYourBag items and total price.
 */
function prepareInYourBagData(cart, inventory) {
  const { c_totalSavings: totalSavings, productItems } = cart;
  const { totalItems, orderItems, totalPrice } = prepareOrderItems(productItems);
  const inYourBagItems = prepareInventoryCartData(inventory, orderItems);
  return { totalItems, inYourBagItems, totalPrice, totalSavings };
}

/**
 *  @description: Fetch data from graphQL cache. If cache is not present then returns undefined. It didn't hit any network call.
 *  @returns: data from graphQL cache and returns the object which contains cart and inventory data.
 */
async function getCartAndInventoryFromCache() {
  let cartCachedData = null;
  let cachedInventory = null;
  try {
    cartCachedData = await getCartDetailsFromCache();
    cachedInventory = await getInventoryFromCache();
  } catch (error) {
    logger.error({
      error: `Error in inYourBagSaga - getCartAndInventoryFromCache catch - ${error}`,
      extraData: {
        componentName: 'InYourBagSaga',
        methodName: 'getCartAndInventoryFromCache',
        isServiceError: error?.isServiceError || false,
      },
    });
  }
  return { cartCachedData, cachedInventory };
}

function* calculateLoyaltyPoints(inYourBagItems, isPLCC, isLoyaltyPointsEnabled) {
  try {
    const { loyaltyMultiplier, loyaltyPLCCMultiplier } = yield select(getLoyaltyPointsMultiplier);
    return inYourBagItems.map((item) => {
      const product = JSON.parse(JSON.stringify(item));
      const {
        itemInfo: { itemUnitDstPrice, isGiftItem },
        loyaltyPromoMultiplierUSStore: itemMprPointsMultiplier,
        loyaltyPromoPLCCMultiplierUSStore: itemPlccPointsMultiplier,
      } = item;
      const mprPointsMultiplier = itemMprPointsMultiplier || loyaltyMultiplier;
      const plccPointsMultiplier = itemPlccPointsMultiplier || loyaltyPLCCMultiplier;
      const pointsMultiplier = isPLCC
        ? parseInt(plccPointsMultiplier, 10)
        : parseInt(mprPointsMultiplier, 10);
      const itemPoints =
        isGiftItem || !isLoyaltyPointsEnabled ? 0 : Math.round(itemUnitDstPrice * pointsMultiplier);
      product.itemInfo.itemPoints = itemPoints;
      return product;
    });
  } catch (error) {
    logger.error({
      error: `Error in inYourBagSaga - getLoyaltyPoints catch - ${error}`,
      extraData: {
        componentName: 'InYourBagSaga',
        methodName: 'getLoyaltyPoints',
        isServiceError: error?.isServiceError || false,
      },
    });
    return inYourBagItems;
  }
}

export function* fetchInYourBagItems({ payload }) {
  let inYourBagItems = [];
  try {
    const { cacheTTL, basketId } = payload;
    const isPLCC = yield select(isPlccUser);
    const isLoyaltyPointsEnabled = yield select(getIsPersistPointsApiEnabled);
    const timestamps = JSON.parse(getLocalStorage(graphQLCacheKey)) || [];
    const cartInventory = yield call(getCartInventoryData, cacheTTL);
    const { cartCachedData, cachedInventory } = yield call(getCartAndInventoryFromCache);
    if (
      !cartInventory ||
      timestamps[inventoryTimestampKey] < timestamps[cartInventoryTimestampKey] ||
      cachedInventory?.productList?.length !== cartInventory?.inv?.productList?.length ||
      cartInventory?.car?.basketId !== basketId
    ) {
      const { inv, car } = yield call(getCartInventoryData, true, cacheTTL);
      inYourBagItems = prepareInYourBagData(car, inv);
    } else if (timestamps[cartTimestampKey] > timestamps[cartInventoryTimestampKey]) {
      inYourBagItems =
        cartCachedData && cachedInventory && prepareInYourBagData(cartCachedData, cachedInventory);
    } else {
      inYourBagItems = prepareInYourBagData(cartInventory?.car, cartInventory?.inv);
    }
    const { totalItems, totalPrice, totalSavings } = inYourBagItems;
    const iybWithLoyaltyPoints = yield calculateLoyaltyPoints(
      inYourBagItems?.inYourBagItems,
      isPLCC,
      isLoyaltyPointsEnabled
    );
    yield put(setInYourBagItems({ totalItems, iybWithLoyaltyPoints, totalPrice, totalSavings }));
  } catch (error) {
    yield put(setInYourBagItems(inYourBagItems));
    logger.error({
      error: `Error in inYourBagSaga - fetchInYourBagItems catch - ${error}`,
      extraData: {
        componentName: 'InYourBagSaga',
        methodName: 'fetchInYourBagItems',
        isServiceError: error?.isServiceError || false,
      },
    });
  }
}

export default function* InYourBagSaga() {
  yield takeLatest(IN_YOUR_BAG.GET_IN_YOUR_BAG_ITEMS, fetchInYourBagItems);
}
